<script lang="ts" setup>
  import { storeToRefs } from 'pinia'

  const { cart } = storeToRefs(useCartStore())

  const bookings = computed(() => cart.value?.bookings ?? [])

  const soldProducts = computed(() => cart.value?.sold_products ?? [])

  const products = computed(() => [...soldProducts.value, ...bookings.value])

  const productsInCartCount = computed(() => products.value.length)

  const dropdownDisplayed = ref(false)

</script>
<template>
  <div
    class="c-cart-menu"
    @mouseleave="dropdownDisplayed = false"
  >
    <Button
      type="secondary"
      @mouseover="dropdownDisplayed = true"
    >
      <div
        v-if="productsInCartCount !== 0"
        class="cart-count"
      >
        {{ productsInCartCount }}
      </div>
      <div class="button-content">
        <SvgIcon
          icon="panier"
          :width="36"
          :height="24"
        />
        <span class="button-text">Panier</span>
      </div>
    </Button>
    <NuxtLink
      to="/shopping-cart/basket"
      class="link"
    >
      <div
        v-if="productsInCartCount !== 0"
        class="cart-count"
      >
        {{ productsInCartCount }}
      </div>
      <div class="button-content">
        <SvgIcon
          icon="panier"
          :width="36"
          :height="24"
        />
        <span class="button-text">Panier</span>
      </div>
    </NuxtLink>
    <div
      v-if="dropdownDisplayed"
      class="dropdown"
    >
      <div class="triangle" />
      <div class="content">
        <template v-if="cart">
          <div class="cards">
            <div
              v-for="product in products"
              :key="product.id"
              class="booking"
            >
              <ShoppingCartProduct
                :booking="product"
                class="header"
                slim
                :is-green-fee="!('category' in product)"
              />
            </div>
          </div>
          <NuxtLink
            v-if="products.length"
            class="go-to-cart"
            to="/shopping-cart/basket"
          >
            <span class="title">
              Voir mon panier <SvgIcon
                icon="arrow-nav"
                :width="24"
                :height="20"
              /></span>
          </NuxtLink>
        </template>
        <template v-else>
          <span class="empty-cart">Vous n'avez aucun article dans votre panier !</span>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped>

.c-cart-menu {
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;

  .link {
    justify-content: center;
    padding: 0.75em 0.5em 0.5em;
    background-color: var(--color-secondary);
    color: white;
  }

  .c-button {
    display: none;
  }

  .c-button,
  .link {
    width: 4em;
    flex: 1 1 auto;
    align-items: center;
    padding-bottom: 0;

    .cart-count {
      position: absolute;
      display: flex;
      width: 1.5em;
      height: 1.5em;
      align-items: center;
      justify-content: center;
      border-radius: 10em;
      background-color: var(--color-promo);
      color: white;
      font-family: Open-Sans, sans-serif;
      font-size: 0.875em;
      font-weight: 600;
      transform: translate(1.2em, -0.6em);
    }

    .button-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .c-icon {
        margin-bottom: 0.25em;
      }

      .button-text {
        color: white;
        font-family: Open-Sans, sans-serif;
        font-size: 0.75em;
        font-weight: 600;
      }
    }
  }

  .dropdown {
    position: absolute;
    z-index: 1;
    top: calc(100% - 6px);
    right: -1em;
    display: none;
    flex-direction: column;
    align-items: flex-end;

    .triangle {
      position: relative;
      z-index: 2;
      right: calc(3em - 8px);
      width: 16px;
      border-width: 0 8px 6px;
      border-style: solid;
      border-right-color: transparent;
      border-bottom-color: white;
      border-left-color: transparent;
    }

    .content {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      padding: 2em;
      border-radius: 0 0 0.25em 0.25em;
      background-color: white;
      box-shadow: 0 0 25px 0 rgb(0 0 0 / 15%);

      .cards {
        max-height: 200px;
        overflow-y: auto;

        .booking {
          width: 600px;

          &:not(:first-child) {
            margin-top: 1em;
          }
        }
      }

      .empty-cart {
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.5em;
        font-weight: bold;
        white-space: nowrap;
      }
    }

    .go-to-cart {
      display: flex;
      height: 100%;
      flex: 0 0 auto;
      flex-direction: row;
      align-items: center;
      align-self: flex-end;
      justify-content: space-between;
      padding: 0.75em 1em;
      margin-top: 1em;
      background-color: var(--color-primary);
      cursor: pointer;
      transition: opacity 0.25s ease-out;

      &:hover {
        .title {
          .c-icon {
            margin-right: 0;
          }
        }
      }

      .title {
        display: flex;
        flex: 1 1 auto;
        flex-direction: row;
        justify-content: space-between;
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 1.375em;
        font-weight: bold;
        letter-spacing: 1.5px;
        text-transform: uppercase;

        .c-icon {
          margin-right: 0.25em;
          transform: rotate(-0.25turn);
          transition: margin-right ease-out 0.25s;
        }
      }
    }
  }

  @media screen and (width >= 800px) {
    .link {
      display: none;
    }

    .c-button {
      display: inherit;
    }

    .dropdown {
      display: flex;
    }
  }

  @media screen and (width >= 1024px) {
    .dropdown {
      top: calc(100% - 10px);

      .triangle {
        right: calc(3em - 12px);
        width: 24px;
        border-width: 0 12px 10px;
      }
    }
  }
}
</style>
